import { useEffect, useId, useRef, useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { BrandMark, Button } from '../ui';
import { UserMenu } from './UserMenu';
import { useFocusTrap } from '../../utils/accessibility';
import { useLogout } from '../../hooks/api/useAuth';
import authService from '../../lib/auth';

/**
 * The public header: wordmark, four destinations, and the two things a visitor
 * can do without an account.
 *
 * "Lacak Pesanan" is a control, not a page — tracking needs a code, and
 * `/order/:code` without one is a dead route. So the nav item opens a small
 * form and navigates only once a code exists; an empty submit stays put.
 *
 * Below 768px everything folds into a drawer. The drawer is only mounted while
 * open, which is what keeps a translated-off-screen panel from widening the
 * body at 375px.
 */

export type NavSurface = 'white' | 'accent';

interface PublicNavProps {
  surface?: NavSurface;
}

const NAV_ITEMS = [
  { label: 'Jastip Belanja', to: '/jasa/belanja' },
  { label: 'Toko', to: '/toko' },
  { label: 'Cek Harga', to: '/cek-harga' },
];

/** Code -> /order/:code. Empty stays put; there is nothing to look up. */
function LacakForm({ onNavigate }: { onNavigate?: () => void }) {
  const navigate = useNavigate();
  const inputId = useId();
  const [code, setCode] = useState('');

  return (
    <form
      className="flex flex-col gap-2"
      onSubmit={(event) => {
        event.preventDefault();
        const trimmed = code.trim();
        if (!trimmed) return;
        onNavigate?.();
        navigate(`/order/${encodeURIComponent(trimmed)}`);
      }}
    >
      <label htmlFor={inputId} className="text-[13px] font-extrabold tracking-[0.05em] text-ink">
        KODE PESANAN
      </label>
      <input
        id={inputId}
        value={code}
        onChange={(event) => setCode(event.target.value)}
        autoComplete="off"
        placeholder="LBA-"
        className="min-h-[44px] rounded-block border-2 border-muted bg-white px-4 font-mono text-[15px] text-ink transition-colors duration-150 ease-out focus-visible:border-ink focus-visible:outline-none"
      />
      <Button type="submit" variant="secondary" fullWidth>
        Lacak
      </Button>
    </form>
  );
}

export function PublicNav({ surface = 'accent' }: PublicNavProps) {
  // Read once per render: login/logout navigate, so the header re-mounts.
  const user = authService.isAuthenticated() ? authService.getUser() : null;
  const navigate = useNavigate();
  const logout = useLogout();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [lacakOpen, setLacakOpen] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const lacakButtonRef = useRef<HTMLButtonElement>(null);
  const drawerRef = useFocusTrap(drawerOpen);

  // Esc closes whichever disclosure is open and hands focus back to its
  // trigger — a keyboard user must not be dropped at the top of the document.
  useEffect(() => {
    if (!drawerOpen && !lacakOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      if (drawerOpen) {
        setDrawerOpen(false);
        menuButtonRef.current?.focus();
      }
      if (lacakOpen) {
        setLacakOpen(false);
        lacakButtonRef.current?.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [drawerOpen, lacakOpen]);

  const onAccent = surface === 'accent';
  const headerClass = onAccent ? 'bg-accent' : 'bg-white border-b-2 border-surface-2';
  const idleLink = onAccent ? 'text-ink opacity-70 hover:opacity-100' : 'text-body hover:text-ink';

  const navLinkClass = ({ isActive }: { isActive: boolean }) =>
    `inline-flex min-h-[44px] items-center text-[15px] font-bold no-underline transition-opacity duration-150 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink ${
      isActive ? 'text-ink' : idleLink
    }`;

  const closeDrawer = () => {
    setDrawerOpen(false);
    menuButtonRef.current?.focus();
  };

  return (
    <header className={headerClass}>
      <div className="mx-auto flex h-[74px] max-w-[1180px] items-center justify-between gap-4 px-5 md:px-16">
        <div className="flex items-center gap-[34px]">
          <Link
            to="/"
            className="focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink"
            aria-label="Jualan, Lapak Bang Ade. Ke beranda"
          >
            <BrandMark variant={onAccent ? 'accent' : 'light'} />
          </Link>

          <nav aria-label="Navigasi utama" className="hidden items-center gap-[26px] md:flex">
            {NAV_ITEMS.map((item) => (
              <NavLink key={item.to} to={item.to} className={navLinkClass}>
                {({ isActive }) => (
                  <span className={isActive ? 'border-b-[2.5px] border-ink pb-[3px]' : ''}>
                    {item.label}
                  </span>
                )}
              </NavLink>
            ))}

            <div className="relative">
              <button
                ref={lacakButtonRef}
                type="button"
                aria-expanded={lacakOpen}
                aria-controls="lacak-pesanan-form"
                onClick={() => setLacakOpen((open) => !open)}
                className={`inline-flex min-h-[44px] items-center text-[15px] font-bold transition-opacity duration-150 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink ${idleLink}`}
              >
                Lacak Pesanan
              </button>
              {lacakOpen && (
                <div
                  id="lacak-pesanan-form"
                  className="absolute right-0 top-full z-40 w-[280px] rounded-block border-2 border-ink bg-white p-4"
                >
                  <LacakForm onNavigate={() => setLacakOpen(false)} />
                </div>
              )}
            </div>
          </nav>
        </div>

        <div className="hidden items-center gap-4 md:flex">
          {user ? (
            <UserMenu
              align="right"
              accountHref="/akun"
              triggerClassName="inline-flex min-h-[44px] items-center text-[15px] font-bold text-ink transition-opacity duration-150 ease-out hover:opacity-70"
              trigger={user.name}
            />
          ) : (
            <Link
              to="/login"
              className="inline-flex min-h-[44px] items-center text-[15px] font-bold text-ink no-underline transition-opacity duration-150 ease-out hover:opacity-70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink"
            >
              Masuk
            </Link>
          )}
          <Button as="a" to="/jasa/belanja">
            Mulai Jastip
          </Button>
        </div>

        <button
          ref={menuButtonRef}
          type="button"
          aria-expanded={drawerOpen}
          aria-controls="nav-drawer"
          aria-label="Buka menu"
          onClick={() => setDrawerOpen(true)}
          className="inline-flex h-[44px] w-[44px] items-center justify-center rounded-pill text-ink transition-colors duration-150 ease-out hover:bg-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink md:hidden"
        >
          <Menu size={22} aria-hidden="true" />
        </button>
      </div>

      {drawerOpen && (
        <>
          <div className="fixed inset-0 z-40 bg-ink/60 md:hidden" onClick={closeDrawer} aria-hidden="true" />
          <div
            id="nav-drawer"
            ref={drawerRef}
            role="dialog"
            aria-modal="true"
            aria-label="Menu"
            className="fixed right-0 top-0 z-50 flex h-full w-[300px] max-w-[85vw] flex-col gap-5 overflow-y-auto bg-white p-5 md:hidden"
          >
            <div className="flex items-center justify-between">
              <BrandMark size="sm" />
              <button
                type="button"
                aria-label="Tutup menu"
                onClick={closeDrawer}
                className="inline-flex h-[44px] w-[44px] items-center justify-center rounded-pill text-ink transition-colors duration-150 ease-out hover:bg-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink"
              >
                <X size={22} aria-hidden="true" />
              </button>
            </div>

            <nav aria-label="Navigasi utama" className="flex flex-col">
              {NAV_ITEMS.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={() => setDrawerOpen(false)}
                  className={({ isActive }) =>
                    `inline-flex min-h-[48px] items-center text-[16px] font-bold no-underline transition-colors duration-150 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink ${
                      isActive ? 'text-ink' : 'text-body hover:text-ink'
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              ))}
            </nav>

            <div className="border-t-2 border-line pt-4">
              <LacakForm onNavigate={() => setDrawerOpen(false)} />
            </div>

            <div className="mt-auto flex flex-col gap-3 border-t-2 border-line pt-4">
              <Button as="a" to="/jasa/belanja" fullWidth onClick={() => setDrawerOpen(false)}>
                Mulai Jastip
              </Button>
              <Button
                as="a"
                to={user ? '/akun' : '/login'}
                variant="secondary"
                fullWidth
                onClick={() => setDrawerOpen(false)}
              >
                {user ? 'Akun saya' : 'Masuk'}
              </Button>
              {user && (
                <Button
                  variant="secondary"
                  fullWidth
                  disabled={logout.isPending}
                  onClick={async () => {
                    await logout.mutateAsync();
                    setDrawerOpen(false);
                    navigate('/');
                  }}
                >
                  {logout.isPending ? 'Keluar…' : 'Keluar'}
                </Button>
              )}
            </div>
          </div>
        </>
      )}
    </header>
  );
}

export default PublicNav;
